import { Trans } from '@lingui/macro'

import { SCREEN_CONFIG } from '@config/screenConfig'
import { useCompanyStylesCtx } from '@contexts/CompanyStylesContext/CompanyStylesProvider'
import { LAYOUT_BREAKPOINTS } from '@segments/Responsive/client'
import { useRouteCtx, useWaiterRouter } from '@segments/Routing/client'
import { BaseHeaderContent, HeaderScreen } from '@segments/ScreenLayout'
import * as React from 'react'

import { ContactUsPhone } from '~shared/components/Phone/ContactUsPhone'
import { SubmitConfirmationMessage, useSubmittedOrderCtx } from '~shared/segments/SubmittedScreen'
import { Button } from '~ui/components/buttons/Button/Button'
import { Box, Grid, GridCentered } from '~ui/components/layout/Box'
import { H1 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'
import { clipPaths } from '~ui/styleGuide/styles'

type TProps = NoChildren & {
  customerNumber: number
}

const maxContentWidth = SCREEN_CONFIG.maxTextWidth

/**
 * Handles mostly the responsive questions on the SubmittedScreen (logic is in the context provider).
 */
export const SubmittedScreenResponsiveContent: React.FC<TProps> = ({ customerNumber }) => {
  const order = useSubmittedOrderCtx()

  const router = useWaiterRouter()
  const { screenRoute } = useRouteCtx()

  // this screen is rendered both as part of the order process and via email link for tracking the order state
  // if it’s the first case (the end of the ordering process) show some confirming message & prioritize other information
  const submittedOrderNow = screenRoute === `order-placed`
  const companyStyles = useCompanyStylesCtx()

  const onBackToHome = React.useCallback(() => {
    router.replace(`home`)
  }, [router])

  return (
    <HeaderScreen
      stickyHeader
      maxContentWidth={`none`}
      headerContent={
        <BaseHeaderContent
          title={companyStyles.primaryNameLabel}
          navigateTo={`home`}
          navigationIcon={`home`}
        />
      }
    >
      <Grid gridTemplateRows={`1fr auto`} height={`100%`}>
        <Box>
          <GridCentered
            padding={{
              left: `medium`,
              right: `medium`,
              top: `extraLarge`,
              bottom: `extraLargePlus`,
            }}
            bgColor={`bgColors.company`}
            width={`100%`}
            clipPath={clipPaths.bottomEllipse}
          >
            <Text fontStyle={`medium`}>order number</Text>
            <H1 lineHeight={`1.2`} fontSize={`l80`} fontStyle={`boldExtra`}>
              {customerNumber}
            </H1>
          </GridCentered>

          {submittedOrderNow && (
            <GridCentered
              top={`-3rem`}
              alignSelf={`center`}
              position={`relative`}
              maxWidth={maxContentWidth}
              margin={{ vertical: `none`, horizontal: `auto` }}
              padding={{ vertical: `none`, horizontal: `largePlus` }}
            >
              <SubmitConfirmationMessage
                message={
                  <Trans>
                    Please stay relaxed until we give you further instructions. We will keep you
                    updated on the progress of your order via your phone number.
                  </Trans>
                }
                screenBgColor={SCREEN_CONFIG.bgColor}
                minHeadingBreakpoint={LAYOUT_BREAKPOINTS.smMin}
                maxHeadingBreakpoint={LAYOUT_BREAKPOINTS.mdMax}
              />
            </GridCentered>
          )}
        </Box>

        <Grid
          justifyItems={`center`}
          gap={`large`}
          margin={{ vertical: `none`, horizontal: `auto` }}
          maxWidth={maxContentWidth}
        >
          {order && (
            <GridCentered width={`90%`}>
              <ContactUsPhone branch={order.branch} />
            </GridCentered>
          )}

          {/* BACK TO HOME BUTTON */}
          <Box
            width={`100%`}
            padding={{
              bottom: `mediumLarge`,
              left: `mediumLarge`,
              right: `mediumLarge`,
              top: `none`,
            }}
          >
            <Button onClick={onBackToHome} size={`large`} width={`100%`} bgColor={`secondary`}>
              <Text fontSize={`m18`} fontStyle={`boldSemi`}>
                <Trans>Back to homepage</Trans>
              </Text>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </HeaderScreen>
  )
}
